import { useQuery } from '@tanstack/react-query'
import type { LinkProps } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import { type Invoice, invoiceDetailQueryOptions } from '@/entities/invoice'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { DeleteInvoiceDialog, EditInvoiceDialog } from '@/features/manage-invoice'
import { InvoiceActions } from '@/features/mark-invoice-paid'
import { useSearchStore } from '@/features/search'
import { ApiError } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { DateStamp } from '@/shared/ui/date-stamp'
import { FileRow } from '@/shared/ui/file-row'
import { Icon } from '@/shared/ui/icon'
import { MoneyAmount } from '@/shared/ui/money-amount'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { CommentThread } from '@/widgets/comment-thread'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'

export const INVOICE_TABS = ['comments', 'docs'] as const
export type InvoiceTab = (typeof INVOICE_TABS)[number]
const TAB_LABELS: Record<InvoiceTab, string> = { comments: 'Комментарии', docs: 'Документы' }

interface InvoicePageProps {
  invoiceId: string
  activeTab: InvoiceTab
  onTabChange: (tab: InvoiceTab) => void
  // Хлебные крошки зависят от контура (проект/компания) — строит роут.
  renderBreadcrumb: (invoice: Invoice) => ReactNode
  // Ссылка на карточку документа счёта — роутинг остаётся у роута.
  docLink: (docId: string) => LinkProps
  /** Куда уйти после удаления счёта. */
  onDeleted: () => void
}

// Страница счёта — реюз в контуре проекта и в контуре прямого счёта компании.
export function InvoicePageView({
  invoiceId,
  activeTab,
  onTabChange,
  renderBreadcrumb,
  docLink,
  onDeleted,
}: InvoicePageProps) {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const invoiceQ = useQuery(invoiceDetailQueryOptions(invoiceId))
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  if (invoiceQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (invoiceQ.error) {
    const e = invoiceQ.error
    if (e instanceof ApiError && e.isForbidden) {
      return <StatusScreen title="Нет доступа" message="Счёт недоступен." />
    }
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Счёт не найден" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить счёт" message="Обновите страницу." />
  }

  const inv = invoiceQ.data

  const tabs: EntityPageTab<InvoiceTab>[] = INVOICE_TABS.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'comments' ? (
        <CommentThread entityKind="invoice" entityId={inv.id} />
      ) : (
        <DocumentSlots entityKind="invoice" entityId={inv.id} docLink={docLink} />
      ),
  }))

  const header = (
    <EntityPageHeader
      breadcrumb={renderBreadcrumb(inv)}
      title={`Счёт ${inv.number}`}
      status={
        <Meta label="Статус">
          <StatusBadge status={inv.overdue ? 'overdue' : inv.status} />
        </Meta>
      }
      meta={
        <>
          <Meta label="Сумма">
            <MoneyAmount kopecks={inv.amount_kopecks} className="text-base" />
          </Meta>
          {inv.due_date && (
            <Meta label="Оплатить до">
              <DateStamp value={inv.due_date} deadline className="text-sm" />
            </Meta>
          )}
          <Meta label="Выставлен">
            <span className="text-sm text-text-secondary">
              {inv.issued_by.name || '—'} ·{' '}
              <DateStamp value={inv.issued_at} zone className="text-sm" />
            </span>
          </Meta>
          {inv.status === 'payment_claimed' && inv.payment_claimed_at && (
            <Meta label="Оплата заявлена">
              <DateStamp value={inv.payment_claimed_at} zone className="text-sm text-warning" />
            </Meta>
          )}
          {inv.status === 'paid' && inv.paid_at && (
            <Meta label="Оплачен">
              <DateStamp value={inv.paid_at} zone className="text-sm text-success" />
            </Meta>
          )}
        </>
      }
      {...(isEmployee
        ? {
            tags: (
              <EntityTagsEditor
                entityKind="invoice"
                entityId={inv.id}
                tags={inv.tags ?? []}
                onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
              />
            ),
          }
        : {})}
      {...(inv.status !== 'paid'
        ? {
            actions: (
              <>
                <InvoiceActions invoiceId={inv.id} status={inv.status} isEmployee={isEmployee} />
                {/* Правка и удаление живут, пока счёт не оплачен: оплаченный
                    заморожен (бэкенд отвечает 409). */}
                {isEmployee && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                      <Icon name="pencil" size={16} />
                      Изменить
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteOpen(true)}>
                      <Icon name="close" size={16} />
                      Удалить
                    </Button>
                  </>
                )}
              </>
            ),
          }
        : {})}
      extra={
        <div className="flex flex-col gap-2 rounded-card bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset">
          <span className="label text-text-secondary">Файл счёта</span>
          <FileRow file={inv.file} />
        </div>
      }
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabsLabel="Разделы счёта"
      />

      {editOpen && <EditInvoiceDialog invoice={inv} open onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeleteInvoiceDialog
          invoice={{ id: inv.id, number: inv.number }}
          open
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </>
  )
}
